import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  Text,
  BlockStack,
  InlineStack,
  Banner,
  Spinner,
  Select,
  FormLayout,
  TextField,
  Checkbox,
} from '@shopify/polaris';
import './App.css';

// Get shop from URL - handles both direct ?shop= and encoded ?host= parameter
function getShop() {
  const params = new URLSearchParams(window.location.search);
  
  // Try direct shop parameter first
  const directShop = params.get('shop');
  if (directShop) return directShop;
  
  // Decode from host parameter (Shopify Admin embeds shop in base64 host)
  const host = params.get('host');
  if (host) {
    try {
      const decoded = atob(host);
      // host decodes to "shop-name.myshopify.com/admin"
      const match = decoded.match(/^([^/]+)/);
      if (match) return match[1];
    } catch (e) {
      console.error('Failed to decode host:', e);
    }
  }
  
  return null;
}

// Language strings (matching OpenCart)
const lang = {
  heading_title: 'IVO Marketplace',
  text_edit: 'IVO Marketplace Settings',
  text_please_configure: 'IVO Marketplace is installed but not configured. Please configure it to start selling on IVO.',
  text_success_configured: 'IVO Marketplace configured successfully!',
  text_syncing: 'Syncing...',
  text_sync_success: 'Products synchronization started successfully.',
  text_select: '--- Select ---',
  entry_api_key: 'API Key',
  entry_merchant_point: 'Merchant Point',
  entry_price_modifier: 'Price Modifier (%)',
  help_price_modifier: 'Adjust product prices synced to IVO. Enter a percentage to increase or decrease (e.g. +10 or -5). Use 0 for no adjustment.',
  button_configure: 'Configure Plugin',
  button_reconfigure: 'Reconfigure',
  button_force_sync: 'Force Sync with IVO',
  button_save: 'Save',
  button_save_settings: 'Save settings',
  error_not_configured: 'Unable to fetch Merchant Point ID. Please check API Key configuration.',
  merchant_point_warning: 'Please select a Merchant Point. The plugin will not sync products until a merchant point is selected.',
  text_connected: 'Connected',
  text_not_connected: 'Not connected',
  text_shopify_bridge: 'Shopify bridge',
  text_connect_title: 'Connect your store to IVO.md.',
  text_connect_description: 'Authorize your marketplace account, choose what gets synced, then let Shopify and IVO work together.',
  text_settings: 'Settings',
  text_account_access: 'Account access',
  text_account_access_help: 'Add your IVO API key and merchant identifier.',
  text_product_sync: 'Product sync',
  text_product_sync_help: 'Map categories, prices, stock, variants, and images.',
  text_update_stock: 'Update stock',
  text_update_stock_help: 'Automatically sync stock between your store and IVO.',
  entry_update_stock: 'Update stock on sale',
  help_update_stock: 'Automatically update Shopify inventory when a sale occurs on IVO.',
  tag_success: 'Success',
  tag_required: 'Required',
  tag_active: 'Active',
  tag_next: 'Next',
  tag_ready: 'Ready',
  tag_inactive: 'Inactive',
};

function App() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [selectedMerchantPoint, setSelectedMerchantPoint] = useState('');
  const [priceModifier, setPriceModifier] = useState('0');
  const [updateStock, setUpdateStock] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const shop = getShop();

  // Check for success/error in URL (from callback)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'configured') {
      setSuccess(lang.text_success_configured);
    }
    if (params.get('error')) {
      const errCode = params.get('error');
      if (errCode === 'no_api_key') {
        setError('No API key received from IVO.');
      } else if (errCode === 'decrypt_failed') {
        setError('Failed to process API key from IVO.');
      } else {
        setError('Configuration failed: ' + errCode);
      }
    }
  }, []);

  // Fetch IVO status on load
  useEffect(() => {
    if (!shop) return;
    fetchStatus();
  }, [shop]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/ivo/status?shop=${encodeURIComponent(shop)}`);
      const data = await res.json();
      setStatus(data);
      
      if (data.merchant_point_id) {
        setSelectedMerchantPoint(data.merchant_point_id);
      } else if (data.merchant_points?.length >= 1) {
        // Auto-select the first merchant point, matching the WordPress plugin.
        setSelectedMerchantPoint(data.merchant_points[0].id);
      }

      if (data.price_modifier !== undefined) {
        setPriceModifier(String(data.price_modifier));
      }
      setUpdateStock(data.update_stock !== false);
    } catch (err) {
      console.error('Failed to fetch status:', err);
      setError('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleConfigure = () => {
    // Open IVO setup in a new window (Shopify CSP blocks framing external URLs)
    const setupUrl = `/ivo/setup?shop=${encodeURIComponent(shop)}`;
    const popup = window.open(setupUrl, '_blank', 'width=800,height=600');
    
    // Remember the current config timestamp to detect NEW configuration
    const startTime = Date.now();
    
    // Poll backend for configuration completion
    const pollInterval = setInterval(async () => {
      try {
        // Check if popup was closed
        if (popup && popup.closed) {
          clearInterval(pollInterval);
          // Final refresh after popup closes
          window.location.reload();
          return;
        }
        
        // Poll backend to check if configuration completed
        const res = await fetch(`/ivo/status?shop=${encodeURIComponent(shop)}`);
        const data = await res.json();
        
        // Check if this is a NEW configuration (updated after we started)
        const updatedAt = data.updated_at ? new Date(data.updated_at).getTime() : 0;
        if (data.configured && updatedAt > startTime) {
          // NEW configuration completed! Close popup and refresh
          clearInterval(pollInterval);
          if (popup && !popup.closed) {
            popup.close();
          }
          // Refresh the entire frame to show updated status
          window.location.reload();
        }
      } catch (e) {
        // Errors are expected, ignore them
      }
    }, 2000); // Check every 2 seconds
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess('');
    setError('');

    try {
      const res = await fetch('/ivo/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop,
          merchant_point_id: selectedMerchantPoint,
          price_modifier: parseFloat(priceModifier) || 0,
          update_stock: updateStock,
          status: true
        })
      });

      const data = await res.json();
      
      if (data.success) {
        setSuccess(data.success);
        fetchStatus();
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (err) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setSuccess('');
    setError('');

    try {
      const res = await fetch('/ivo/sync-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop })
      });

      const data = await res.json();
      
      if (data.success) {
        setSuccess(data.success);
      } else {
        setError(data.error || 'Failed to sync products');
      }
    } catch (err) {
      setError('Failed to sync products');
    } finally {
      setSyncing(false);
    }
  };

  if (!shop) {
    return (
      <Page title={lang.heading_title}>
        <Card>
          <Banner status="critical">
            <p>Missing shop parameter. Please access this app from Shopify admin.</p>
          </Banner>
        </Card>
      </Page>
    );
  }

  if (loading) {
    return (
      <Page title={lang.heading_title}>
        <Card>
          <BlockStack gap="400" align="center">
            <Spinner size="large" />
            <Text>Loading...</Text>
          </BlockStack>
        </Card>
      </Page>
    );
  }

  const renderSetupCard = (children, connected = false) => (
    <div className="ivo-card">
      <div className="ivo-card__head">
        <div className="ivo-card__icon">IVO</div>
        <strong>{lang.heading_title}</strong>
        <div className={`ivo-card__status ${connected ? 'ivo-card__status--connected' : ''}`}>
          <span className={`ivo-card__status-dot ${connected ? 'ivo-card__status-dot--connected' : ''}`} />
          {connected ? lang.text_connected : lang.text_not_connected}
        </div>
      </div>
      <div className="ivo-card__body">
        <div className="ivo-card__main">
          <div className="ivo-card__logo">
            <img src="https://static.ivo.md/favicon_white.png" alt="IVO" />
          </div>
          {children}
        </div>
        <div className="ivo-card__setup">
          <div className="ivo-card__setup-box">
            <div className="ivo-card__step">
              <div className="ivo-card__step-number">1</div>
              <div>
                <strong>{lang.text_account_access}</strong>
                <span>{lang.text_account_access_help}</span>
              </div>
              <div className={`ivo-card__tag ${connected ? 'ivo-card__tag--success' : 'ivo-card__tag--active'}`}>
                {connected ? lang.tag_success : lang.tag_required}
              </div>
            </div>
            <div className="ivo-card__step">
              <div className="ivo-card__step-number">2</div>
              <div>
                <strong>{lang.text_product_sync}</strong>
                <span>{lang.text_product_sync_help}</span>
              </div>
              <div className={`ivo-card__tag ${connected ? 'ivo-card__tag--success' : ''}`}>
                {connected ? lang.tag_active : lang.tag_next}
              </div>
            </div>
            <div className="ivo-card__step">
              <div className="ivo-card__step-number">3</div>
              <div>
                <strong>{lang.text_update_stock}</strong>
                <span>{lang.text_update_stock_help}</span>
              </div>
              <div className={`ivo-card__tag ${connected && updateStock ? 'ivo-card__tag--success' : ''}`}>
                {connected ? (updateStock ? lang.tag_active : lang.tag_inactive) : lang.tag_ready}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // NOT CONFIGURED: Show big Configure button (like OpenCart)
  if (!status?.configured) {
    return (
      <Page title={lang.heading_title}>
        <Layout>
          <Layout.Section>
            {renderSetupCard(
              <>
                <p className="ivo-card__eyebrow">{lang.text_shopify_bridge}</p>
                <h2>{lang.text_connect_title}</h2>
                <p>{lang.text_connect_description}</p>
                <div className="ivo-card__actions">
                  <button type="button" className="ivo-card__button" onClick={handleConfigure}>
                    {lang.button_configure}
                  </button>
                </div>
              </>,
              false
            )}
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // CONFIGURED BUT API ERROR: Show error and Reconfigure button (like OpenCart)
  if (status?.configured && !status?.api_valid) {
    return (
      <Page title={lang.heading_title}>
        <Layout>
          <Layout.Section>
            <Banner status="critical" title="IVO API Error">
              <p>{status.api_error}</p>
            </Banner>
          </Layout.Section>
          <Layout.Section>
            <Card>
              <BlockStack gap="400" align="center">
                <Text>{lang.entry_api_key}: {status.api_key_masked}</Text>
                <Button variant="primary" onClick={handleConfigure}>
                  {lang.button_reconfigure}
                </Button>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  // CONFIGURED AND API VALID: Show settings form (like OpenCart)
  const merchantPointOptions = [
    { label: lang.text_select, value: '' },
    ...(status?.merchant_points || []).map(p => ({
      label: p.address ? `${p.name} - ${p.address}` : p.name,
      value: p.id
    }))
  ];

  const showMerchantPointWarning = !selectedMerchantPoint && (status?.merchant_points?.length > 1);

  return (
    <Page title={lang.heading_title}>
      <Layout>
        {success && (
          <Layout.Section>
            <Banner status="success" onDismiss={() => setSuccess('')}>
              <p>{success}</p>
            </Banner>
          </Layout.Section>
        )}

        {error && (
          <Layout.Section>
            <Banner status="critical" onDismiss={() => setError('')}>
              <p>{error}</p>
            </Banner>
          </Layout.Section>
        )}

        {showMerchantPointWarning && (
          <Layout.Section>
            <Banner status="warning">
              <p>{lang.merchant_point_warning}</p>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          {renderSetupCard(
            <>
              <p className="ivo-card__eyebrow">{lang.text_shopify_bridge}</p>
              <h2>{lang.text_settings}</h2>
              <div className="ivo-form-shell">
                <FormLayout>
                  <InlineStack gap="200" align="start">
                    <Text variant="bodyMd" fontWeight="semibold">{lang.entry_api_key}:</Text>
                    <Text variant="bodyMd">{status.api_key_masked}</Text>
                  </InlineStack>

                  <Select
                    label={lang.entry_merchant_point}
                    options={merchantPointOptions}
                    value={selectedMerchantPoint}
                    onChange={setSelectedMerchantPoint}
                  />

                  <TextField
                    type="number"
                    label={lang.entry_price_modifier}
                    value={priceModifier}
                    onChange={setPriceModifier}
                    helpText={lang.help_price_modifier}
                    autoComplete="off"
                  />

                  <Checkbox
                    label={lang.entry_update_stock}
                    helpText={lang.help_update_stock}
                    checked={updateStock}
                    onChange={setUpdateStock}
                  />

                  <InlineStack gap="200">
                    <button type="button" className="ivo-card__button" onClick={handleSave} disabled={saving}>
                      {saving ? 'Saving...' : lang.button_save_settings}
                    </button>
                    <button type="button" className="ivo-card__button-secondary" onClick={handleConfigure}>
                      {lang.button_reconfigure}
                    </button>
                    <button type="button" className="ivo-card__button-secondary" onClick={handleSync} disabled={syncing}>
                      {syncing ? lang.text_syncing : lang.button_force_sync}
                    </button>
                  </InlineStack>
                </FormLayout>
              </div>
            </>,
            true
          )}
        </Layout.Section>

      </Layout>
    </Page>
  );
}

export default App;
